<template>
    <div
        class="signature-status-badge"
        :class="[statusClass, sizeClass]"
        :title="tooltipText"
    >
        <i :class="statusIcon"></i>
        <span class="status-text">{{ statusText }}</span>
        <span v-if="showPercentage && completionPercentage !== null" class="percentage">
            {{ completionPercentage }}%
        </span>
    </div>
</template>

<script>
export default {
    name: 'SignatureStatusBadge',
    props: {
        signatureRequired: {
            type: Boolean,
            default: false
        },
        completionPercentage: {
            type: Number,
            default: null
        },
        pendingCount: {
            type: Number,
            default: 0
        },
        size: {
            type: String,
            default: 'medium',
            validator: (value) => ['small', 'medium', 'large'].includes(value)
        },
        showPercentage: {
            type: Boolean,
            default: true
        }
    },
    computed: {
        /*
         * The backend's completion_percentage now reflects *all* template
         * fields (signatures AND data fields). The badge's label used to
         * say "Fully Signed" / "Partially Signed" / "Awaiting Signatures"
         * which is misleading for mixed templates (e.g. a doc with all
         * signatures signed but data fields unfilled would render
         * "Fully Signed 50%"). Drive the label off completionPercentage
         * directly and use neutral progress language. The backend still
         * has DocumentInstance::isFullySigned() for the audit-trail
         * concept (used by submit/flatten flows), but this badge does
         * not need it.
         */
        statusClass() {
            if (!this.signatureRequired) {
                return 'status-not-required';
            }
            if (this.completionPercentage === 100) {
                return 'status-completed';
            }
            if (this.completionPercentage > 0) {
                return 'status-in-progress';
            }
            return 'status-pending';
        },
        sizeClass() {
            return `size-${this.size}`;
        },
        statusIcon() {
            if (!this.signatureRequired) {
                return 'fa fa-minus-circle';
            }
            if (this.completionPercentage === 100) {
                return 'fa fa-check-circle';
            }
            if (this.completionPercentage > 0) {
                return 'fa fa-clock';
            }
            return 'fa fa-hourglass-start';
        },
        statusText() {
            if (!this.signatureRequired) {
                return 'No Signatures Required';
            }
            if (this.completionPercentage === 100) {
                return 'Completed';
            }
            if (this.completionPercentage > 0) {
                return 'In Progress';
            }
            return 'Not Started';
        },
        tooltipText() {
            if (!this.signatureRequired) {
                return 'This document does not require signatures';
            }
            if (this.completionPercentage === 100) {
                return 'All fields are complete';
            }
            if (this.completionPercentage !== null) {
                return `${this.completionPercentage}% complete - ${this.pendingCount} field(s) pending`;
            }
            return `${this.pendingCount} field(s) pending`;
        }
    }
};
</script>

<style scoped>
.signature-status-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 16px;
    font-weight: 600;
    transition: all 0.2s;
    cursor: default;
}

/* Status Colors */
.status-not-required {
    background-color: #e2e8f0;
    color: #718096;
}

.status-pending {
    background-color: #fef3c7;
    color: #92400e;
    border: 1px solid #fcd34d;
}

.status-in-progress {
    background-color: #dbeafe;
    color: #1e40af;
    border: 1px solid #60a5fa;
}

.status-completed {
    background-color: #d1fae5;
    color: #065f46;
    border: 1px solid #34d399;
}

/* Size Variants */
.size-small {
    font-size: 11px;
    padding: 4px 8px;
    gap: 4px;
}

.size-small i {
    font-size: 10px;
}

.size-medium {
    font-size: 13px;
    padding: 6px 12px;
    gap: 6px;
}

.size-medium i {
    font-size: 12px;
}

.size-large {
    font-size: 15px;
    padding: 8px 16px;
    gap: 8px;
}

.size-large i {
    font-size: 14px;
}

.status-text {
    white-space: nowrap;
}

.percentage {
    font-weight: 700;
    margin-left: 2px;
}

/* Mobile Optimization */
@media (max-width: 768px) {
    .signature-status-badge {
        font-size: 12px;
    }

    .status-text {
        display: none;
    }

    .size-small .status-text,
    .size-medium .status-text {
        display: none;
    }

    .size-large .status-text {
        display: inline;
    }
}
</style>
